import React, { ComponentProps, useEffect } from 'react';
import {
  BlockInteractions,
  createButtonObject,
  DISPLAY_TYPE,
  Views,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import Event from '../../lib/EventEmitter';

import CurrentUserInformation from '../../components/molecules/CurrentUserInformation/CurrentUserInformation';

import { Namespace } from '../../translation/namespace';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { ISonURL } from '../../types/IUrl';
import { MembersListView as DefaultView } from './partials/MembersList/MembersList';
import { MembersManage as MembersManageView } from './partials/MembersManage/MembersManage';
import { TeamroomInviteView } from '../../components/forms/TeamroomInvite/TeamroomInvite';
import { InstantView, LazyView } from '../../types/BlockWithViews';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { AddUserView } from './partials/AddUser/AddUser';

type MembersType = {
  personalProfileLink: ISonURL;
  activeView?: string;
  views: {
    default: InstantView<any>;
    manage: LazyView;
    add: LazyView;
    invite: LazyView;
    inviteUnregistered: LazyView;
  };
};

export function Members({ personalProfileLink, activeView, views }: MembersType) {
  const { t } = useSonTranslation('Members');
  const { uuid, elementType } = useBlockContext();
  const { delayedRefresh } = useRefreshBlock();
  const { editModeInfo } = useAppContext();

  useEffect(() => {
    Event.register('refetch-data', (issuer: string) => {
      if (issuer === 'RequestManagement') {
        delayedRefresh();
      }
    });

    return () => {
      Event.unRegister('refetch-data', delayedRefresh);
    };
  }, [delayedRefresh]);

  const createButtonsObject = () => {
    const AddButton = createButtonObject(
      Views.ADD,
      Icons.ADD_PLUS,
      `${Namespace.TeamroomManagement}:actions.add`,
      DISPLAY_TYPE.MODAL,
      <AddUserView
        {...views[Views.ADD]}
        additionalProps={{
          translationNamespace: `${Namespace.TeamroomManagement}:actions.add`,
        }}
      />,
    );

    const InviteButton = createButtonObject(
      Views.INVITE,
      Icons.ADD_USER_PLUS,
      `${Namespace.TeamroomManagement}:actions.invite`,
      DISPLAY_TYPE.MODAL,
      <TeamroomInviteView
        {...views[Views.INVITE]}
        additionalProps={{
          translationNamespace: `${Namespace.TeamroomManagement}:actions.invite`,
        }}
      />,
    );

    const InviteUnregisteredButton = createButtonObject(
      Views.INVITE_UNREGISTERED,
      Icons.INVITE_UNKNOWN_USER,
      `${Namespace.TeamroomManagement}:actions.inviteUnregistered`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <TeamroomInviteView
        {...views[Views.INVITE_UNREGISTERED]}
        additionalProps={{
          translationNamespace: `${Namespace.TeamroomManagement}:actions.inviteUnregistered`,
        }}
      />,
    );

    return Object.assign(InviteButton || {}, InviteUnregisteredButton, AddButton);
  };

  const createInfoText = () => {
    return personalProfileLink
      ? (
          <CurrentUserInformation profileLink={personalProfileLink}>{t('actionbar.infotext')}</CurrentUserInformation>
        )
      : null;
  };

  /**
   * Users can only edit, when rolesWithStatus are available.
   * */
  const getActiveView = () => {
    if (!editModeInfo.active || (editModeInfo.active && !views.default?.embedded?.members[0]?.rolesWithStatus)) {
      return <DefaultView {...views.default} />;
    }
    return <MembersManageView {...views.default} />;
  };

  return (
    <BlockInteractions
      headline={{
        text: t('actionbar.headline'),
        hierarchy: 3,
        visible: false,
      }}
      info={createInfoText()}
      alwaysShowInfo={false}
      views={views}
      activeView={activeView}
      buttons={createButtonsObject()}
      uuid={uuid}
      elementType={elementType}
    >
      {getActiveView()}
    </BlockInteractions>
  );
}

export const MembersBlock = asFetchableBlock(Members, BlockType.areamembers);
export type MembersBlock = ComponentProps<typeof MembersBlock>;
